import { useEffect, useState } from "react";
import { useNavigate } from "react-router-dom";
import { toast } from "sonner";
import { Plus, Trash2, Users, Sparkles, ArrowUpRight, Check, Calendar as CalendarIcon } from "lucide-react";
import { format } from "date-fns";
import api, { formatApiError } from "../lib/api";
import { getServices } from "../lib/servicesCache";
import { useAuth } from "../context/AuthContext";
import { useSettings } from "../context/SettingsContext";
import { Calendar } from "../components/ui/calendar";
import { Popover, PopoverTrigger, PopoverContent } from "../components/ui/popover";

const emptyMember = (postal = "") => ({
    _id: (typeof crypto !== "undefined" && crypto.randomUUID) ? crypto.randomUUID() : `m-${Date.now()}-${Math.random().toString(36).slice(2, 8)}`,
    name: "",
    phone: "",
    address_line: "",
    postal_code: postal,
    service_ids: [],
    package_ids: [],
    variant_selections: {},
});

export default function GroupBookingPage() {
    const { user } = useAuth();
    const { settings } = useSettings();
    const navigate = useNavigate();
    const minGroupTotal = settings.group_min_total ?? 80;
    const rewardThreshold = settings.group_reward_threshold ?? 500;
    const leadMinutes = Math.max(0, parseInt(settings.min_booking_lead_minutes ?? 60, 10) || 0);

    const [services, setServices] = useState([]);
    const [packages, setPackages] = useState([]);
    const [members, setMembers] = useState([
        // Initiator pre-filled from logged-in user
        emptyMember(""),
        emptyMember(""),
    ]);
    const [date, setDate] = useState(null);
    const [datePopoverOpen, setDatePopoverOpen] = useState(false);
    const [time, setTime] = useState("");
    const [notes, setNotes] = useState("");
    const [submitting, setSubmitting] = useState(false);
    const [result, setResult] = useState(null);

    useEffect(() => {
        getServices().then((data) => setServices(data));
        api.get("/packages", { params: { active_only: true } }).then((r) => setPackages(r.data || [])).catch(() => {});
    }, []);

    useEffect(() => {
        if (!user) return;
        setMembers((prev) => {
            const copy = [...prev];
            if (!copy[0].name) {
                copy[0] = {
                    ...copy[0],
                    name: user.name || "",
                    phone: (user.phone || "").replace(/^\+65/, ""),
                    address_line: user.address_line || "",
                    postal_code: user.postal_code || "",
                };
            }
            return copy;
        });
    }, [user]);

    const svcMap = Object.fromEntries(services.map((s) => [s.id, s]));
    const pkgMap = Object.fromEntries(packages.map((p) => [p.id, p]));
    const priceOfFor = (member, sid) => {
        const s = svcMap[sid];
        if (!s) return 0;
        const variants = s.variants || [];
        if (variants.length === 0) return Number(s.price);
        const v = variants.find((x) => x.id === (member.variant_selections || {})[sid]);
        return v ? Number(v.price) : Number(s.price);
    };
    const memberPkgSubtotal = (m) => (m.package_ids || []).reduce((sum, pid) => sum + Number(pkgMap[pid]?.price || 0), 0);
    const memberSubtotal = (m) => (m.service_ids || []).reduce((sum, sid) => sum + priceOfFor(m, sid), 0) + memberPkgSubtotal(m);
    const memberMissingVariants = (m) => (m.service_ids || []).filter((sid) => (svcMap[sid]?.variants || []).length > 0 && !(m.variant_selections || {})[sid]);
    const groupTotal = members.reduce((sum, m) => sum + memberSubtotal(m), 0);
    const firstPostal = (members[0].postal_code || "").trim();
    const samePostal = members.every((m) => (m.postal_code || "").trim() === firstPostal);
    const reachedMin = groupTotal >= minGroupTotal;
    const reachedReward = groupTotal >= rewardThreshold;
    const allVariantsPicked = members.every((m) => memberMissingVariants(m).length === 0);

    const updateMember = (idx, patch) => {
        setMembers((prev) => prev.map((m, i) => (i === idx ? { ...m, ...patch } : m)));
    };

    const toggleService = (idx, sid) => {
        setMembers((prev) => prev.map((m, i) => i === idx ? {
            ...m,
            service_ids: m.service_ids.includes(sid) ? m.service_ids.filter((x) => x !== sid) : [...m.service_ids, sid],
        } : m));
    };

    const togglePackage = (idx, pid) => {
        setMembers((prev) => prev.map((m, i) => i === idx ? {
            ...m,
            package_ids: (m.package_ids || []).includes(pid) ? (m.package_ids || []).filter((x) => x !== pid) : [...(m.package_ids || []), pid],
        } : m));
    };

    const setMemberVariant = (idx, sid, vid) => {
        setMembers((prev) => prev.map((m, i) => i === idx ? {
            ...m,
            variant_selections: { ...(m.variant_selections || {}), [sid]: vid },
        } : m));
    };

    const addMember = () => setMembers((prev) => [...prev, emptyMember(prev[0].postal_code || "")]);
    const removeMember = (idx) => setMembers((prev) => prev.length > 2 ? prev.filter((_, i) => i !== idx) : prev);

    // ---- Same-day lead-time guard ----
    // `now` is held in state and refreshed every 30 seconds so that as real-world
    // time advances past a slot's lead-time window, the picker re-evaluates and
    // drops the now-invalid slot automatically (without requiring page reload).
    const [now, setNow] = useState(() => new Date());
    useEffect(() => {
        const id = setInterval(() => setNow(new Date()), 30000);
        return () => clearInterval(id);
    }, []);

    const ALL_SLOTS = ["10:00","11:00","12:00","13:00","14:00","15:00","16:00","17:00","18:00","19:00","20:00"];
    const isToday = (() => {
        if (!date) return false;
        return date.getFullYear() === now.getFullYear() && date.getMonth() === now.getMonth() && date.getDate() === now.getDate();
    })();
    const availableSlots = (() => {
        if (!isToday) return ALL_SLOTS;
        const cutoff = now.getHours() * 60 + now.getMinutes() + leadMinutes;
        return ALL_SLOTS.filter((slot) => {
            const [h, m] = slot.split(":").map(Number);
            return h * 60 + m >= cutoff;
        });
    })();
    // If user switches to today and their chosen slot becomes invalid, drop it.
    useEffect(() => {
        if (time && !availableSlots.includes(time)) {
            setTime("");
        }
    }, [date, leadMinutes, time, availableSlots, now]);

    // Visible value for the <select>: if the held `time` is no longer in the
    // filtered slots, render the placeholder instead of a stale value.
    const visibleTime = availableSlots.includes(time) ? time : "";

    const leadLabel = (() => {
        if (leadMinutes <= 0) return null;
        const h = Math.floor(leadMinutes / 60);
        const m = leadMinutes % 60;
        if (h && m) return `${h}h ${m}m`;
        if (h) return `${h} hour${h !== 1 ? "s" : ""}`;
        return `${m} minute${m !== 1 ? "s" : ""}`;
    })();

    const submit = async (e) => {
        e.preventDefault();
        if (!user) { toast.error("Please sign in to create a group booking"); navigate("/login"); return; }
        if (!samePostal) { toast.error("All members must share the same postal code. Please regroup."); return; }
        if (!allVariantsPicked) { toast.error("Pick an option for every service that has sub-options"); return; }
        if (!reachedMin) { toast.error(`Group total S$${groupTotal.toFixed(2)} is below the minimum S$${minGroupTotal}`); return; }
        for (const m of members) {
            const hasSvc = (m.service_ids || []).length > 0;
            const hasPkg = (m.package_ids || []).length > 0;
            if (!m.name || !m.phone || !m.address_line || !m.postal_code || (!hasSvc && !hasPkg)) {
                toast.error("Each member needs name, phone, address, postal code and at least one service or package");
                return;
            }
        }
        if (!date || !time) { toast.error("Pick a date and time for the group"); return; }
        if (isToday && !availableSlots.includes(time)) {
            toast.error(leadLabel ? `Bookings need at least ${leadLabel} lead time — please pick a later slot` : "That time has passed — please pick a later slot");
            return;
        }
        setSubmitting(true);
        // 1) Place the booking — this is the ONLY call whose failure means "booking failed".
        let bookingData = null;
        let bookingErr = null;
        try {
            const payload = {
                members: members.map(({ _id, ...m }) => ({
                    ...m,
                    phone: m.phone.startsWith("+65") ? m.phone : `+65${m.phone.replace(/\D/g, "")}`,
                    service_ids: m.service_ids || [],
                    package_ids: m.package_ids || [],
                    variant_selections: m.variant_selections || {},
                })),
                date: format(date, "yyyy-MM-dd"), time, notes,
            };
            const { data } = await api.post("/group-bookings", payload);
            bookingData = data;
        } catch (err) {
            bookingErr = err;
        }
        if (bookingErr) {
            toast.error(formatApiError(bookingErr.response?.data?.detail, "Group booking failed"));
            setSubmitting(false);
            return;
        }
        // 2) Booking succeeded — every downstream side-effect is best-effort so a render glitch
        //    cannot trap the customer on a blank/half-rendered checkout page.
        try { toast.success(`Group booking placed for ${bookingData.members} members${bookingData.threshold_met ? " — reward unlocked!" : ""}`); } catch { /* noop */ }
        try { setResult(bookingData); } catch { /* noop */ }
        setSubmitting(false);
    };

    if (result) {
        return <GroupBookingSuccess result={result} svcMap={svcMap} onDone={() => navigate("/dashboard")} />;
    }

    return (
        <div className="min-h-screen pt-32 pb-32 grain" data-testid="group-booking-page">
            <div className="max-w-5xl mx-auto px-6 lg:px-12">
                <div className="text-overline flex items-center gap-2"><Users size={12} /> Group Booking</div>
                <h1 className="font-serif-display text-4xl sm:text-5xl text-[#3D0C1F] mt-3">Book together. Save together.</h1>
                <p className="text-[#7A5262] mt-3 max-w-2xl">
                    Add friends or family sharing the same postal code, combine your services, and unlock perks when the group total crosses S${rewardThreshold}.
                </p>

                {/* Rules card */}
                <div className="mt-8 card-luxe p-6 grain text-xs text-[#7A5262] space-y-1.5">
                    <p><span className="text-[#8F622C]">•</span> Minimum 2 members. Each member can pick different services.</p>
                    <p><span className="text-[#8F622C]">•</span> Group total must reach <span className="gold-text">S${minGroupTotal}</span> across all members combined.</p>
                    <p><span className="text-[#8F622C]">•</span> All members must share the same postal code (same address or building).</p>
                    <p><span className="text-[#8F622C]">•</span> Cross <span className="gold-text">S${rewardThreshold}</span> and the group leader gets a complimentary service this visit + a single-use coupon for next booking.</p>
                </div>

                <form onSubmit={submit} className="mt-10 space-y-6">
                    {members.map((m, idx) => (
                        <div key={m._id} className="card-luxe p-6 grain space-y-4" data-testid={`group-member-${idx}`}>
                            <div className="flex items-center justify-between">
                                <div className="text-overline flex items-center gap-2">
                                    {idx === 0 ? <Sparkles size={12} className="text-[#8F622C]" /> : <Users size={12} />}
                                    Member {idx + 1} {idx === 0 && <span className="text-[#8F622C] ml-2">· Group Leader</span>}
                                </div>
                                {members.length > 2 && idx > 0 && (
                                    <button type="button" onClick={() => removeMember(idx)} className="text-[#7A5262] hover:text-red-700" data-testid={`remove-member-${idx}`}>
                                        <Trash2 size={14} />
                                    </button>
                                )}
                            </div>
                            <div className="grid md:grid-cols-2 gap-3">
                                <input required placeholder="Full name" value={m.name} onChange={(e) => updateMember(idx, { name: e.target.value })}
                                    className="bg-[#FBF6F0] border border-[#6B1535]/15 px-3 py-2.5 text-sm text-[#3D0C1F] focus:border-[#C29A5B] outline-none" data-testid={`member-name-${idx}`} />
                                <div className="flex">
                                    <span className="inline-flex items-center px-3 bg-[#FBF6F0] border border-r-0 border-[#6B1535]/15 text-[#8F622C] text-sm">+65</span>
                                    <input required inputMode="numeric" maxLength={8} placeholder="Phone (8 digits)" value={m.phone}
                                        onChange={(e) => updateMember(idx, { phone: e.target.value.replace(/\D/g, "").slice(0, 8) })}
                                        className="flex-1 bg-[#FBF6F0] border border-[#6B1535]/15 px-3 py-2.5 text-sm text-[#3D0C1F] focus:border-[#C29A5B] outline-none" data-testid={`member-phone-${idx}`} />
                                </div>
                            </div>
                            <div className="grid md:grid-cols-3 gap-3">
                                <input required placeholder="Address line" value={m.address_line} onChange={(e) => updateMember(idx, { address_line: e.target.value })}
                                    className="md:col-span-2 bg-[#FBF6F0] border border-[#6B1535]/15 px-3 py-2.5 text-sm text-[#3D0C1F] focus:border-[#C29A5B] outline-none" data-testid={`member-address-${idx}`} />
                                <input required placeholder="Postal code" value={m.postal_code} onChange={(e) => updateMember(idx, { postal_code: e.target.value })}
                                    className={`bg-[#FBF6F0] border px-3 py-2.5 text-sm text-[#3D0C1F] focus:border-[#C29A5B] outline-none ${m.postal_code && m.postal_code !== firstPostal ? "border-red-500/60" : "border-[#6B1535]/15"}`}
                                    data-testid={`member-postal-${idx}`} />
                            </div>
                            {m.postal_code && m.postal_code !== firstPostal && (
                                <p className="text-xs text-red-700" data-testid={`postal-mismatch-${idx}`}>
                                    Postal code must match member 1 ({firstPostal}). Please regroup with people at the same address.
                                </p>
                            )}

                            <div>
                                <div className="text-[0.65rem] tracking-[0.22em] uppercase text-[#7A5262] mb-2">Services</div>
                                <div className="grid sm:grid-cols-2 gap-2 max-h-56 overflow-y-auto pr-2">
                                    {services.map((s) => {
                                        const sel = m.service_ids.includes(s.id);
                                        const variants = s.variants || [];
                                        const priceLabel = variants.length > 0
                                            ? `from S$${Math.min(...variants.map((v) => Number(v.price)))}`
                                            : `S$${s.price}`;
                                        return (
                                            <button key={s.id} type="button" onClick={() => toggleService(idx, s.id)}
                                                className={`text-left px-3 py-2 text-xs border ${sel ? "border-[#C29A5B] bg-[#6B1535]/10 text-[#3D0C1F]" : "border-[#6B1535]/15 text-[#7A5262] hover:border-[#6B1535]/20"}`}
                                                data-testid={`member-${idx}-service-${s.id}`}>
                                                <div className="flex items-center justify-between gap-2">
                                                    <span className="truncate">{s.name}</span>
                                                    <span className="flex items-center gap-2 flex-shrink-0">
                                                        <span className="text-[#8F622C]">{priceLabel}</span>
                                                        {sel && <Check size={12} className="text-[#8F622C]" />}
                                                    </span>
                                                </div>
                                            </button>
                                        );
                                    })}
                                </div>
                            </div>

                            {packages.length > 0 && settings.booking_packages_enabled === true && (
                                <div>
                                    <div className="text-[0.65rem] tracking-[0.22em] uppercase text-[#8F622C] mb-2 flex items-center gap-2"><Sparkles size={10}/> Special Packages</div>
                                    <div className="grid sm:grid-cols-2 gap-2">
                                        {packages.map((p) => {
                                            const sel = (m.package_ids || []).includes(p.id);
                                            return (
                                                <button key={p.id} type="button" onClick={() => togglePackage(idx, p.id)}
                                                    className={`text-left px-3 py-2 text-xs border ${sel ? "border-[#C29A5B] bg-[#6B1535]/10 text-[#3D0C1F]" : "border-[#C29A5B]/25 text-[#7A5262] hover:border-[#C29A5B]/50"}`}
                                                    data-testid={`member-${idx}-package-${p.id}`}>
                                                    <div className="flex items-center justify-between gap-2">
                                                        <span className="truncate">{p.name}</span>
                                                        <span className="flex items-center gap-2 flex-shrink-0">
                                                            <span className="text-[#8F622C]">S${p.price}</span>
                                                            {sel && <Check size={12} className="text-[#8F622C]" />}
                                                        </span>
                                                    </div>
                                                </button>
                                            );
                                        })}
                                    </div>
                                </div>
                            )}

                            {m.service_ids.some((sid) => (svcMap[sid]?.variants || []).length > 0) && (
                                <div className="space-y-3 pt-3 border-t border-[#6B1535]/10" data-testid={`member-${idx}-variants`}>
                                    <div className="text-[0.6rem] tracking-[0.2em] uppercase text-[#7A5262] flex items-center gap-2">
                                        <Sparkles size={10} className="text-[#8F622C]" /> Pick options for {m.name || `Member ${idx + 1}`}
                                    </div>
                                    {m.service_ids
                                        .filter((sid) => (svcMap[sid]?.variants || []).length > 0)
                                        .map((sid) => {
                                            const s = svcMap[sid];
                                            const chosenVid = (m.variant_selections || {})[sid];
                                            return (
                                                <div key={sid} className="space-y-1.5" data-testid={`member-${idx}-variant-row-${sid}`}>
                                                    <div className="text-xs text-[#3D0C1F] flex items-center gap-2">
                                                        {s.name} {!chosenVid && <span className="text-red-700 text-[0.6rem]">required</span>}
                                                    </div>
                                                    <div className="flex flex-wrap gap-1.5">
                                                        {(s.variants || []).map((v) => {
                                                            const isChosen = chosenVid === v.id;
                                                            return (
                                                                <button
                                                                    key={v.id}
                                                                    type="button"
                                                                    onClick={() => setMemberVariant(idx, sid, v.id)}
                                                                    className={`flex items-center gap-1.5 px-2.5 py-1.5 text-[0.7rem] border transition-colors ${isChosen ? "border-[#C29A5B] bg-[#6B1535]/15 text-[#3D0C1F]" : "border-[#6B1535]/15 text-[#7A5262] hover:border-[#6B1535]/25"}`}
                                                                    data-testid={`member-${idx}-variant-${sid}-${v.id}`}
                                                                >
                                                                    <span className={`w-2.5 h-2.5 rounded-full border ${isChosen ? "bg-[#6B1535] border-[#C29A5B]" : "border-[#6B1535]/30"}`} />
                                                                    {v.name}
                                                                    <span className="text-[#8F622C]">S${v.price}</span>
                                                                </button>
                                                            );
                                                        })}
                                                    </div>
                                                </div>
                                            );
                                        })}
                                </div>
                            )}
                            <div className="flex justify-end text-sm pt-3 border-t border-[#6B1535]/10">
                                <span className="text-[#7A5262] mr-3">Subtotal</span>
                                <span className="gold-text font-medium" data-testid={`member-subtotal-${idx}`}>S${memberSubtotal(m).toFixed(2)}</span>
                            </div>
                        </div>
                    ))}

                    <button type="button" onClick={addMember} className="btn-outline-gold w-full justify-center" data-testid="add-member">
                        <Plus size={14} /> Add another member
                    </button>

                    <div className="card-luxe p-6 grain space-y-4">
                        <div className="grid md:grid-cols-2 gap-3">
                            <div>
                                <label className="text-overline">Date</label>
                                <Popover open={datePopoverOpen} onOpenChange={setDatePopoverOpen}>
                                    <PopoverTrigger asChild>
                                        <button
                                            type="button"
                                            className="w-full flex items-center justify-between bg-[#FBF6F0] border border-[#6B1535]/15 px-3 py-2.5 mt-2 text-sm text-left text-[#3D0C1F] focus:border-[#C29A5B] hover:border-[#C29A5B]/40 outline-none transition-colors"
                                            data-testid="group-date"
                                        >
                                            <span className={date ? "text-[#3D0C1F]" : "text-[#8A6574]"}>
                                                {date ? format(date, "EEE, dd MMM yyyy") : "Select a date"}
                                            </span>
                                            <CalendarIcon size={14} className="text-[#8F622C]" />
                                        </button>
                                    </PopoverTrigger>
                                    <PopoverContent
                                        align="start"
                                        className="p-0 bg-[#FBF6F0] border border-[#C29A5B]/30 text-[#3D0C1F] grain"
                                        data-testid="group-date-popover"
                                    >
                                        <Calendar
                                            mode="single"
                                            selected={date}
                                            onSelect={(d) => { setDate(d); if (d) setDatePopoverOpen(false); }}
                                            disabled={(d) => d < new Date(new Date().setHours(0, 0, 0, 0))}
                                            initialFocus
                                            classNames={{
                                                day_selected: "bg-[#6B1535] text-[#FFF8F2] hover:bg-[#6B1535] focus:bg-[#6B1535]",
                                                day_today: "border border-[#C29A5B]/50 text-[#8F622C]",
                                                day: "h-8 w-8 p-0 font-normal text-[#3D0C1F] hover:bg-[#6B1535]/10 rounded-none",
                                                day_outside: "text-[#C9B3BA]",
                                                day_disabled: "text-[#C9B3BA] opacity-40",
                                                caption_label: "text-sm font-medium text-[#3D0C1F] tracking-wider",
                                                head_cell: "text-[#8A6574] w-8 font-normal text-[0.65rem] tracking-[0.2em] uppercase",
                                                nav_button: "h-7 w-7 bg-transparent p-0 text-[#7A5262] hover:text-[#8F622C] hover:bg-transparent border-0",
                                            }}
                                        />
                                    </PopoverContent>
                                </Popover>
                            </div>
                            <div>
                                <label className="text-overline">Time</label>
                                <select required value={visibleTime} onChange={(e) => setTime(e.target.value)}
                                    className="w-full bg-[#FBF6F0] border border-[#6B1535]/15 px-3 py-2.5 mt-2 text-sm text-[#3D0C1F] focus:border-[#C29A5B] outline-none" data-testid="group-time">
                                    <option value="">Select...</option>
                                    {availableSlots.map((t) => <option key={t} value={t}>{t}</option>)}
                                </select>
                                {isToday && availableSlots.length === 0 && (
                                    <p className="text-[0.65rem] text-[#7A5262] mt-2">
                                        {leadLabel
                                            ? `No slots today meet the ${leadLabel} lead time — please pick another date.`
                                            : "No slots left today — please pick another date."}
                                    </p>
                                )}
                                {isToday && availableSlots.length > 0 && leadLabel && (
                                    <p className="text-[0.65rem] text-[#7A5262] mt-2">Same-day slots need at least {leadLabel} lead time.</p>
                                )}
                            </div>
                        </div>
                        <textarea value={notes} onChange={(e) => setNotes(e.target.value)} placeholder="Notes for the team (optional)" rows={2}
                            className="w-full bg-[#FBF6F0] border border-[#6B1535]/15 px-3 py-2.5 text-sm text-[#3D0C1F] focus:border-[#C29A5B] outline-none" data-testid="group-notes" />
                    </div>

                    {/* Live status */}
                    <div className="card-luxe p-6 grain space-y-3" data-testid="group-status">
                        <div className="flex items-center justify-between">
                            <span className="text-overline">Group total</span>
                            <span className="font-serif-display text-3xl gold-text">S${groupTotal.toFixed(2)}</span>
                        </div>
                        <div className="space-y-2 text-xs">
                            <div className="flex items-center gap-2">
                                <span className={`w-2 h-2 rounded-full ${samePostal ? "bg-[#6B1535]" : "bg-red-500"}`} />
                                <span className={samePostal ? "text-[#7A5262]" : "text-red-700"}>
                                    {samePostal ? "All members share the same postal code" : "Postal codes don't match yet"}
                                </span>
                            </div>
                            <div className="flex items-center gap-2">
                                <span className={`w-2 h-2 rounded-full ${reachedMin ? "bg-[#6B1535]" : "bg-[#6B1535]/20"}`} />
                                <span className={reachedMin ? "text-[#7A5262]" : "text-[#8A6574]"}>
                                    {reachedMin ? `Minimum S$${minGroupTotal} reached` : `Add S$${(minGroupTotal - groupTotal).toFixed(2)} more to qualify`}
                                </span>
                            </div>
                            <div className="flex items-center gap-2">
                                <Sparkles size={11} className={reachedReward ? "text-[#8F622C]" : "text-[#8A6574]"} />
                                <span className={reachedReward ? "gold-text" : "text-[#8A6574]"}>
                                    {reachedReward ? "🎉 Reward unlocked — complimentary service + one-time coupon" : `Add S$${(rewardThreshold - groupTotal).toFixed(2)} more to unlock the reward`}
                                </span>
                            </div>
                        </div>
                    </div>

                    <button type="submit" disabled={submitting || !samePostal || !reachedMin || !allVariantsPicked}
                        className="btn-gold w-full justify-center disabled:opacity-40 disabled:cursor-not-allowed text-sm"
                        data-testid="group-submit">
                        {submitting ? "Reserving group…" : "Confirm Group Booking"} <ArrowUpRight size={14} />
                    </button>
                </form>
            </div>
        </div>
    );
}

function GroupBookingSuccess({ result, svcMap, onDone }) {
    const copyCode = (code) => {
        navigator.clipboard?.writeText(code);
        toast.success(`Coupon ${code} copied`);
    };

    return (
        <div className="min-h-screen pt-32 pb-32 grain" data-testid="group-success">
            <div className="max-w-2xl mx-auto px-6 lg:px-12 text-center">
                <div className="inline-flex items-center justify-center w-16 h-16 rounded-full bg-[#6B1535]/15 border border-[#C29A5B]/40">
                    <Check size={28} className="text-[#8F622C]" />
                </div>
                <h1 className="font-serif-display text-4xl sm:text-5xl text-[#3D0C1F] mt-6">Group booked.</h1>
                <p className="text-[#7A5262] mt-3">{result.members} appointments reserved. Awaiting admin confirmation.</p>

                {result.threshold_met && (
                    <div className="mt-10 card-luxe p-8 grain border-[#C29A5B]/50 text-left">
                        <div className="text-overline flex items-center gap-2 justify-center"><Sparkles size={12} className="text-[#8F622C]" /> Reward Unlocked</div>
                        {result.comp_added && result.comp_service_id && (
                            <div className="mt-5 pt-5 border-t border-[#6B1535]/10">
                                <p className="text-sm text-[#7A5262]">Complimentary on this visit (Group Leader):</p>
                                <p className="font-serif-display text-2xl text-[#3D0C1F] mt-1">{svcMap[result.comp_service_id]?.name || "Free service"}</p>
                            </div>
                        )}
                        {result.issued_coupon && (
                            <div className="mt-5 pt-5 border-t border-[#6B1535]/10">
                                <p className="text-sm text-[#7A5262]">One-time coupon for your next booking:</p>
                                <button type="button" onClick={() => copyCode(result.issued_coupon.code)}
                                    className="mt-3 px-5 py-3 border border-[#C29A5B]/50 bg-[#6B1535]/5 hover:bg-[#6B1535]/10 transition-colors font-mono text-lg tracking-wider gold-text w-full"
                                    data-testid="group-reward-coupon">
                                    {result.issued_coupon.code}
                                </button>
                                <p className="text-[0.65rem] text-[#8A6574] mt-2">Single use only. Tap to copy. Apply at your next checkout.</p>
                            </div>
                        )}
                    </div>
                )}

                <button onClick={onDone} className="btn-gold mt-10" data-testid="group-go-dashboard">
                    Go to dashboard <ArrowUpRight size={14} />
                </button>
            </div>
        </div>
    );
}
